import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightSmall, ChatFill, LikeFill } from '@peacecover/icon';
import { useLikeAdaptedFictionEdition } from '../../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useManageCreateScene } from '../hooks/useManageCreateScene';
import { useGetCastRelationshipById } from '../../../domains/scene/ui/hooks/useGetCastRelationshipById';
import { getCastRelationshipId } from '../utils/getCastRelationshipId';
import { getPreviewBackgroundColor } from '../utils/getPreviewBackgroundColor';
import { getChatDirection } from '../utils/getChatDirection';
import { getSceneTags } from '../utils/getSceneTags';
import { getSpeakerOrderMap, getThreadSpeakerColor } from '../utils/getThreadSpeakerColor';
import PreviewHeaderItem from './PreviewHeaderItem';
import ThreadContentItem from './ThreadContentItem';
import ChatContentItem from './ChatContentItem';
import { ReactComponent as ThreadIcon } from '../../../icons/thread.svg';
import type { components } from '../../../types/schema';

type AdaptedFictionPreview = components['schemas']['ApiV1AdaptedFictionPreview'];
type ChatIntroContentType = components['schemas']['ApiV1AdaptedFictionChatContent'];
type ThreadIntroContentType = components['schemas']['ApiV1AdaptedFictionThreadContent'];

interface SwipePreviewCardProps {
  preview: AdaptedFictionPreview;
  onOpenCommentBottomSheet: () => void;
}

const SwipePreviewCard = ({ preview, onOpenCommentBottomSheet }: SwipePreviewCardProps) => (
  <div className="relative w-full h-full">
    <PreviewCard preview={preview} onOpenCommentBottomSheet={onOpenCommentBottomSheet} />
  </div>
);

export default SwipePreviewCard;

const PreviewCard = ({
  preview,
  onOpenCommentBottomSheet
}: {
  preview: AdaptedFictionPreview;
  onOpenCommentBottomSheet: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { handleCreateScene } = useManageCreateScene(preview);
  const navigate = useNavigate();

  const introContents = preview?.introContents ?? [];

  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();

  const currentCastRelationship = getCastRelationshipId();
  const { data: castRelationship } = useGetCastRelationshipById(currentCastRelationship);

  const handleLikeAdaptedFictionEdition = async () => {
    if (!preview.adaptedFictionEditionId) return;
    try {
      const requestBody = {
        engagement_contexts: [
          {
            context_type: 'AdaptedFictionPreview' as const,
            context_id: preview.adaptedFictionPreviewId
          },
          {
            context_type: 'CastRelationship' as const,
            context_id: currentCastRelationship
          }
        ]
      };
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId: preview.adaptedFictionEditionId,
        body: requestBody
      });
    } catch (error) {
      console.error(error);
    }
  };

  const speakerOrderMap = getSpeakerOrderMap(introContents);
  const threadIntroContents = introContents as ThreadIntroContentType[];
  const threadPreviewSlice = threadIntroContents.slice(0, 3);
  const hasMoreThreadIntro = threadIntroContents.length > 3;
  const lastThreadPreviewItem = threadPreviewSlice.at(-1);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 전체 카드 클릭은 `<button>` 래퍼로 두면 하단 버튼과 중첩됨
    <div
      onClick={handleCreateScene}
      className="w-full h-full flex flex-col max-w-[540px] text-left cursor-pointer"
    >
      <div
        className="mt-[11px] overflow-hidden flex-1 min-h-0 pb-[67px]"
        style={{
          clipPath: 'inset(0 0 67px 0)'
        }}
      >
        <PreviewHeaderItem
          isR19={preview?.ageRating === 'R-19'}
          tags={getSceneTags(preview ?? null)}
          className="mb-[16px]"
        />
        <div className="relative w-full overflow-hidden">
          <div
            className="absolute z-0 pointer-events-none top-[5px] left-0 right-0 h-[calc(100vh-77px-110px)] overflow-hidden"
            style={
              {
                '--ball-bg-odd': `${getPreviewBackgroundColor(preview?.sequenceNo ?? 0)}3d`,
                '--ball-bg-even': `${getPreviewBackgroundColor(preview?.sequenceNo ?? 0)}33`
              } as React.CSSProperties
            }
          >
            <span className="ball" />
            <span className="ball" />
            <span className="ball" />
            <span className="ball" />
            <span className="ball" />
            <span className="ball" />
          </div>
          <div className="relative z-10 min-h-[calc(100vh-77px-110px)]">
            {preview?.previewType === 'SceneChat' ? (
              (introContents as ChatIntroContentType[])?.map((introContent, index) => (
                <ChatContentItem
                  key={`${introContent?.text}-${index}`}
                  chatType={introContent?.chatType}
                  text={introContent?.text}
                  pictureUrl={introContent?.pictureUrl}
                  direction={getChatDirection(
                    castRelationship ?? null,
                    introContent?.speaker ?? ''
                  )}
                  className="first:bg-gradient-to-b first:from-black bg-transparent"
                />
              ))
            ) : (
              <>
                {threadPreviewSlice.map((introContent, index) => (
                  <ThreadContentItem
                    key={`${introContent?.text}-${index}`}
                    speaker={introContent?.speaker ?? undefined}
                    threadType={introContent?.threadType}
                    text={introContent?.text}
                    backgroundColor={getThreadSpeakerColor(
                      speakerOrderMap.get(introContent?.speaker ?? '') ?? 0
                    )}
                    className="first:bg-gradient-to-b first:from-black bg-transparent"
                  />
                ))}
                {hasMoreThreadIntro && lastThreadPreviewItem ? (
                  <ThreadContentItem
                    key="thread-preview-more"
                    speaker={lastThreadPreviewItem.speaker}
                    threadType="thread"
                    text={t('SceneMain.threadPreviewMore')}
                    backgroundColor={getThreadSpeakerColor(
                      speakerOrderMap.get(lastThreadPreviewItem.speaker) ?? 0
                    )}
                    hasLine={false}
                    className="bg-transparent"
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-[67px] left-0 w-full h-[300px] overflow-hidden flex flex-col justify-end max-w-[540px]"
        style={{
          background: `linear-gradient(180deg, rgba(108, 67, 255, 0.00) 0%, ${getPreviewBackgroundColor(preview?.sequenceNo ?? 0)} 83.94%)`
        }}
        role="presentation"
      >
        <div className="absolute inset-0 z-0" aria-hidden />
        <div className="relative h-full z-10 pointer-events-none">
          <div className="absolute right-[17px] top-[10px] flex flex-col items-center gap-[16px]">
            <button
              type="button"
              className="mb-[6px] pointer-events-auto relative"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/fanhearts?path=fanfic&locale=${encodeURIComponent(i18n.language)}`);
              }}
            >
              <img
                src="/assets/scene/recruit_author_icon.png"
                alt="author"
                className="w-[46px] h-[46px] rounded-full object-cover"
              />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-[-8px] whitespace-nowrap px-[6px] py-[2px] rounded-[30px] bg-graident-single-purple text-[11px] leading-[13px] text-pure-white font-semibold animate-gradient-flow">
                {t('SceneMain.recruitAuthor')}
              </div>
            </button>
            <button
              type="button"
              className="flex flex-col items-center pointer-events-auto"
              onClick={(e) => {
                e.stopPropagation();
                handleLikeAdaptedFictionEdition();
              }}
            >
              <LikeFill
                size={32}
                className="text-pure-white"
                gradientStops={
                  preview?.isLiked
                    ? [
                        { offset: '0%', color: '#FF45A9' },
                        { offset: '100%', color: '#FF6A6A' }
                      ]
                    : []
                }
              />
              <p className="text-[11px] leading-[13px] text-pure-white font-semibold">
                {preview?.likesCount}
              </p>
            </button>
            <button
              type="button"
              className="flex flex-col items-center pointer-events-auto"
              onClick={(e) => {
                e.stopPropagation();
                onOpenCommentBottomSheet();
              }}
            >
              <ChatFill size={32} className="text-pure-white" />
              <p className="text-[11px] leading-[13px] text-pure-white font-semibold">
                {preview?.commentsCount}
              </p>
            </button>
          </div>
        </div>
        <div className="ml-[24px] mb-[79px] flex items-center gap-[8px] py-[4px] pl-[8px] pr-[4px] rounded-[3px] w-fit pointer-events-auto z-10 cursor-pointer bg-[rgba(0,0,0,0.1)] border-0 text-left">
          <p className="text-[14px] font-bold leading-[17px] text-pure-white max-w-[159px] truncate">
            {preview?.fictionTitle}
          </p>
          <div className="flex items-center gap-[4px]">
            {preview?.previewType === 'SceneThread' ? (
              <ThreadIcon width={14} height={14} />
            ) : (
              <ChatFill size={14} className="text-pure-white" />
            )}
            <p className="text-[12px] font-medium leading-[14px] text-pure-white">
              {preview?.previewType === 'SceneThread'
                ? t('SceneMain.threadPreviewCount', { count: preview?.contentsCount })
                : t('SceneMain.chatPreviewCount', { count: preview?.contentsCount })}
            </p>
            <ArrowRightSmall size={14} className="text-pure-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
